/**
 * Header Component - Layout
 * Top header bar with app name, role indicator, and user menu
 * Uses Tailwind for layout styling
 */

import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, LogOut, User, ChevronDown, Moon, Sun, Target, BarChart3, Tag, FileText, PieChart, TrendingUp, Receipt, Briefcase, Clock, Banknote, GitCompare, Shield, Calculator, CreditCard, History, Settings, Bell, FolderOpen, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useLogout } from '@/hooks/useLogout';
import { useTheme } from '@/hooks/useTheme';
import { useAppStore } from '@/stores/app.store';
import { useAudience } from '@/hooks/useAudience';
import { cn } from '@/lib/utils';

const MODULE_MAP: { prefix: string; label: string; icon: LucideIcon }[] = [
  // Specific routes BEFORE catch-alls
  { prefix: '/backoffice/clientes', label: 'CLIENTES', icon: Users },
  { prefix: '/backoffice/expedientes', label: 'CLIENTES', icon: FolderOpen },
  { prefix: '/credit/active', label: 'PORTAFOLIO', icon: CreditCard },
  { prefix: '/credit/scoring', label: 'ORIGINACION', icon: Target },
  { prefix: '/credit/pipeline', label: 'ORIGINACION', icon: BarChart3 },
  { prefix: '/credit/offers', label: 'ORIGINACION', icon: Tag },
  { prefix: '/credit/applications', label: 'ORIGINACION', icon: FileText },
  { prefix: '/credit/', label: 'ORIGINACION', icon: BarChart3 },
  { prefix: '/portfolio/historico', label: 'PORTAFOLIO', icon: TrendingUp },
  { prefix: '/portfolio/', label: 'PORTAFOLIO', icon: PieChart },
  { prefix: '/collection/cartera', label: 'COBRANZA', icon: Briefcase },
  { prefix: '/collection/latent', label: 'COBRANZA', icon: Clock },
  { prefix: '/collection/', label: 'COBRANZA', icon: Receipt },
  { prefix: '/funding/', label: 'FONDEO', icon: Banknote },
  { prefix: '/ops/definitions', label: 'OPERACIONES', icon: Settings },
  { prefix: '/ops/reconciliation', label: 'OPERACIONES', icon: GitCompare },
  { prefix: '/ops/alerts', label: 'OPERACIONES', icon: Bell },
  { prefix: '/ops/', label: 'OPERACIONES', icon: Settings },
  { prefix: '/config/', label: 'CONFIGURACION', icon: History },
  { prefix: '/policies', label: 'CONFIGURACION', icon: Shield },
  { prefix: '/tools/', label: 'CONFIGURACION', icon: Calculator },
];

export function Header() {
  const { user } = useAuth();
  const logout = useLogout();
  const { toggle, theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const toggleSidebar = useAppStore((s) => s.toggleSidebar);
  const { resolvedRole } = useAudience();
  const location = useLocation();
  const currentModule = MODULE_MAP.find((m) => location.pathname.startsWith(m.prefix));
  const ModuleIcon = currentModule?.icon;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const displayRole = resolvedRole === 'BACKOFFICE' ? 'Fiado SA' : 'UNE-Credito';

  return (
    <header className="flex h-16 items-center justify-between border-b border-border bg-card px-4 lg:px-6">
      {/* Left: hamburger + title */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggleSidebar}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground lg:hidden"
          aria-label="Toggle sidebar"
        >
          <Menu size={20} />
        </button>
        {currentModule ? (
          <div className="flex items-center gap-2">
            {ModuleIcon && <ModuleIcon size={18} className="text-muted-foreground" />}
            <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{currentModule.label}</span>
          </div>
        ) : (
          <h1 className="text-lg font-semibold text-foreground">Fiado Credit</h1>
        )}
      </div>

      {/* Right: theme toggle + role badge + user dropdown */}
      <div className="flex items-center gap-4">
        {/* Theme toggle */}
        <button
          type="button"
          onClick={toggle}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Toggle theme"
          title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Role indicator */}
        <span className="hidden rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 sm:inline-flex">
          {displayRole}
        </span>

        {/* User dropdown */}
        <div ref={dropdownRef} className="relative">
          <button
            type="button"
            onClick={() => setDropdownOpen((prev) => !prev)}
            className={cn(
              'flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors',
              'text-foreground hover:opacity-80',
              dropdownOpen && 'opacity-80'
            )}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-300">
              {user?.email?.charAt(0).toUpperCase() ?? 'U'}
            </div>
            <span className="hidden max-w-[140px] truncate md:block">
              {user?.email ?? 'User'}
            </span>
            <ChevronDown size={14} className="text-muted-foreground" />
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-card py-1 shadow-lg">
              <div className="border-b border-border px-4 py-2.5">
                <p className="text-sm font-medium text-foreground">{user?.email}</p>
                <p className="text-xs text-muted-foreground">{displayRole}</p>
              </div>
              {/* Mi perfil — deep-link cross-app al backoffice RBAC (abre pestaña nueva).
                  SSO sin re-login se apoya en la cookie de sesión compartida .tufiado.com.
                  Mismo patrón que fiado-agents-webapp (spec mi-perfil-redirect-rbac). */}
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground transition-colors hover:bg-muted"
                onClick={() => {
                  setDropdownOpen(false);
                  globalThis.open(
                    `${import.meta.env.VITE_ACCOUNT_URL}/me/profile`,
                    '_blank',
                    'noopener,noreferrer'
                  );
                }}
              >
                <User size={16} className="text-muted-foreground" />
                Profile
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => {
                  setDropdownOpen(false);
                  logout();
                }}
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
